/**
 * OrgSwitcher — Header dropdown for super-admins to switch the active org.
 *
 * When an org is selected:
 *   1. adminStore.selectedOrgId is updated (persisted in localStorage)
 *   2. The API client automatically injects X-Tenant-Id on all future requests
 *   3. Tenant context is re-fetched so displayed org name updates immediately
 *   4. All TanStack Query caches are invalidated to reload data for the new org
 *
 * Renders nothing for non-super-admin sessions.
 */

import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronDown, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAdminStore } from "../lib/admin";
import { useTenantStore } from "../lib/tenant";

export function OrgSwitcher() {
  const isSuperAdmin = useAdminStore((s) => s.isSuperAdmin);
  const selectedOrgId = useAdminStore((s) => s.selectedOrgId);
  const organizations = useAdminStore((s) => s.organizations);
  const setSelectedOrgId = useAdminStore((s) => s.setSelectedOrgId);
  const fetchOrganizations = useAdminStore((s) => s.fetchOrganizations);

  const tenantOrgName = useTenantStore((s) => s.org_name);
  const fetchTenantContext = useTenantStore((s) => s.fetchTenantContext);

  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const canSwitchOrgs = isSuperAdmin;

  useEffect(() => {
    if (canSwitchOrgs) {
      fetchOrganizations();
    }
  }, [canSwitchOrgs, fetchOrganizations]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!canSwitchOrgs) return null;

  const currentOrg = organizations.find((o) => o.org_id === selectedOrgId);
  const displayLabel =
    selectedOrgId === null
      ? (tenantOrgName ?? "Default (hostname)")
      : (currentOrg?.org_name ?? `Org #${selectedOrgId}`);

  async function handleSelect(orgId: number | null) {
    setOpen(false);
    setSelectedOrgId(orgId);
    await fetchTenantContext();
    await queryClient.invalidateQueries();
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        role="combobox"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-md border border-light bg-surface px-2.5 py-1.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Building2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
        {/* @allow-arbitrary */}
        <span className="max-w-[140px] truncate">{displayLabel}</span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 dark:text-gray-400" />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-72 overflow-hidden rounded-lg border border-light bg-surface shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-3 py-2 dark:border-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">Switch Organization</span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>

          <div role="listbox" className="max-h-72 overflow-y-auto py-1">
            <div>
              <button
                type="button"
                role="option"
                aria-selected={selectedOrgId === null}
                onClick={() => handleSelect(null)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                <span className="h-2 w-2 flex-shrink-0 rounded-full bg-brand-400" />
                <span className="flex-1 font-medium text-gray-700 dark:text-gray-200">
                  {tenantOrgName ?? "Default (hostname)"}
                </span>
                {selectedOrgId === null && (
                  <Check className="h-3.5 w-3.5 flex-shrink-0 text-brand-500 dark:text-brand" />
                )}
              </button>
            </div>

            {organizations.length > 0 && <div className="mx-3 my-1 border-t border-gray-100 dark:border-gray-700" />}

            {organizations.map((org) => (
              <div key={org.org_id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedOrgId === org.org_id}
                  onClick={() => handleSelect(org.org_id)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  <span
                    className={`h-2 w-2 flex-shrink-0 rounded-full ${
                      org.is_active ? "bg-green-400" : "bg-gray-300 dark:bg-gray-600"
                    }`}
                  />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate font-medium text-gray-800 dark:text-gray-100">{org.org_name}</span>
                    <span className="block text-xs text-muted">
                      {org.contact_count.toLocaleString()} contacts &middot; {org.campaign_count.toLocaleString()}{" "}
                      campaigns &middot; {org.plan_id}
                    </span>
                  </span>
                  {selectedOrgId === org.org_id && (
                    <Check className="h-3.5 w-3.5 flex-shrink-0 text-brand-500 dark:text-brand" />
                  )}
                </button>
              </div>
            ))}

            {organizations.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-400 dark:text-gray-400">No organizations found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
